import { forwardRef, useMemo } from 'react'
import { Box } from '@mui/material'
import { tokens } from '@agribot/polygon-checks-ui'
import type { GroupStructure as Structure } from '../types'

/**
 * A supplier group the way the buyer's RFI draws it (Figure 4): the group
 * company at the top, its companies and legal entities below, each entity's
 * raw materials, and at the bottom the origins, each drawn once and joined to
 * every material that uses it, so an origin shared across legal entities
 * ("Origin 3" in the RFI) is plain to see. Below the origins, the farmer
 * cooperatives behind them, with their plots' check results: the way down to
 * the polygons. Pure SVG, to save as an image.
 */

const BOX_H = 24
const MAT_W = 132
const ORI_W = 132
const GAP_X = 16
const ROW_GAP = 64
const PAD = 16
const FONT = `12px ${tokens.font.sans}`

let ctx: CanvasRenderingContext2D | null | undefined
function fit(text: string, width: number): string {
  if (ctx === undefined) {
    try { ctx = document.createElement('canvas').getContext('2d') } catch { ctx = null }
    if (ctx) ctx.font = FONT
  }
  const measure = (s: string) => (ctx ? ctx.measureText(s).width : s.length * 6.4)
  if (measure(text) <= width) return text
  let s = text
  while (s.length > 1 && measure(`${s}…`) > width) s = s.slice(0, -1)
  return `${s}…`
}

interface Box_ { x: number; y: number; w: number; label: string; full: string; fill: string; text: string; stroke?: string; bold?: boolean }

export const GroupStructure = forwardRef<SVGSVGElement, { structure: Structure; countryName: (code: string) => string }>(
  function GroupStructure({ structure, countryName }, ref) {
    const layout = useMemo(() => {
      const { company, entities, origins } = structure
      const children = company.children ?? []
      const tiers = children.length ? 1 : 0
      const y = (row: number) => PAD + 22 + row * (BOX_H + ROW_GAP)
      const boxes: Box_[] = []
      const lines: { a: Box_; b: Box_; strong?: boolean }[] = []

      // Legal entities side by side, each as wide as its materials.
      let x = PAD
      const entityBox = new Map<string, Box_>()
      const materialBox = new Map<string, Box_>()
      for (const e of entities) {
        const w = Math.max(1, e.materials.length) * (MAT_W + GAP_X) - GAP_X
        const eb: Box_ = {
          x, y: y(1 + tiers), w, label: fit(e.name, w - 14), full: `${e.name} (${countryName(e.countryCode)})`,
          fill: tokens.color.ink0, text: tokens.color.ink700, stroke: tokens.color.ink400, bold: true,
        }
        entityBox.set(e.id, eb)
        boxes.push(eb)
        e.materials.forEach((m, i) => {
          const mb: Box_ = {
            x: x + i * (MAT_W + GAP_X), y: y(2 + tiers), w: MAT_W, label: fit(m.name, MAT_W - 14), full: `${m.name} (${m.code})`,
            fill: tokens.color.tintDanger, text: '#7a3020',
          }
          materialBox.set(m.id, mb)
          boxes.push(mb)
          lines.push({ a: eb, b: mb })
        })
        x += w + GAP_X * 3
      }
      const width = Math.max(x - GAP_X * 3 + PAD, 360)

      // Companies in the group: the top one, and its subsidiaries over their entities.
      const top: Box_ = { x: width / 2 - 100, y: y(0), w: 200, label: fit(company.name, 186), full: company.name, fill: tokens.color.brandDeep, text: tokens.color.ink0, bold: true }
      boxes.push(top)
      const companyBox = new Map<string, Box_>([[company.id, top]])
      const under = (id: string): string => {
        // Entities of deeper companies hang from the nearest child of the top company.
        for (const c of children) {
          const ids = new Set<string>()
          const walk = (n: typeof c) => { ids.add(n.id); (n.children ?? []).forEach(walk) }
          walk(c)
          if (ids.has(id)) return c.id
        }
        return company.id
      }
      for (const c of children) {
        const own = entities.filter((e) => under(e.parentCompanyId) === c.id).map((e) => entityBox.get(e.id)!)
        if (!own.length) continue
        const left = Math.min(...own.map((b) => b.x))
        const right = Math.max(...own.map((b) => b.x + b.w))
        const w = Math.min(220, right - left)
        const cb: Box_ = { x: (left + right) / 2 - w / 2, y: y(1), w, label: fit(c.name, w - 14), full: c.name, fill: tokens.color.brandTeal100, text: '#18576e', bold: true }
        companyBox.set(c.id, cb)
        boxes.push(cb)
        lines.push({ a: top, b: cb })
      }
      for (const e of entities) lines.push({ a: companyBox.get(under(e.parentCompanyId)) ?? top, b: entityBox.get(e.id)! })

      // Origins under the materials that use them, then spread so none overlap.
      const usedBy = new Map<string, Box_[]>()
      for (const e of entities) for (const m of e.materials) for (const id of m.originIds) {
        usedBy.set(id, [...(usedBy.get(id) ?? []), materialBox.get(m.id)!])
      }
      const placed = origins
        .filter((o) => usedBy.has(o.id))
        .map((o) => {
          const ms = usedBy.get(o.id)!
          const centre = ms.reduce((n, b) => n + b.x + b.w / 2, 0) / ms.length
          return { o, ms, x: centre - ORI_W / 2 }
        })
        .sort((a, b) => a.x - b.x)
      let next = PAD
      for (const p of placed) { p.x = Math.max(p.x, next); next = p.x + ORI_W + GAP_X }
      const overflow = Math.max(0, next - GAP_X + PAD - width)
      const total = width + overflow
      const originBox = new Map<string, Box_>()
      for (const p of placed) {
        const shared = p.ms.length > 1
        const failed = p.o.check.state === 'failed'
        // The count first, so a long name never truncates it away.
        const label = `${shared ? `×${p.ms.length} ` : ''}${p.o.name}`
        const ob: Box_ = {
          x: p.x, y: y(3 + tiers), w: ORI_W, label: fit(`${failed ? '⚠ ' : ''}${label}`, ORI_W - 14),
          full: `${p.o.name}, ${countryName(p.o.countryCode)}${shared ? ` — used by ${p.ms.length} materials` : ''}${failed && p.o.check.state === 'failed' ? ` — ${p.o.check.reason}` : ''}`,
          fill: failed ? tokens.color.tintDanger : tokens.color.tintWarning, text: failed ? '#7a3020' : '#6b4c12',
          stroke: shared ? tokens.color.warning : undefined, bold: shared,
        }
        boxes.push(ob)
        originBox.set(p.o.id, ob)
        for (const m of p.ms) lines.push({ a: m, b: ob, strong: shared })
      }

      // The cooperatives behind the origins, each once, under the origins it supplies.
      const coops = new Map<string, { name: string; plots: number; rejected: number; yieldT?: number; under: Box_[] }>()
      for (const p of placed) {
        for (const c of p.o.supply?.organisationList ?? []) {
          const e = coops.get(c.id) ?? { name: c.name, plots: c.plots, rejected: c.rejected, yieldT: c.yieldT, under: [] }
          e.under.push(originBox.get(p.o.id)!)
          coops.set(c.id, e)
        }
      }
      const coopRow = 4 + tiers
      let nextCoop = PAD
      const coopPlaced = [...coops.values()]
        .map((c) => ({ c, x: c.under.reduce((n, b) => n + b.x + b.w / 2, 0) / c.under.length - ORI_W / 2 }))
        .sort((a, b) => a.x - b.x)
      for (const cp of coopPlaced) {
        cp.x = Math.max(cp.x, nextCoop)
        nextCoop = cp.x + ORI_W + GAP_X
        const tonnes = cp.c.yieldT ? ` · ${Math.round(cp.c.yieldT).toLocaleString('en-GB')} t` : ''
        const label = `${cp.c.name} · ${cp.c.plots} plots${tonnes}`
        const cb: Box_ = {
          x: cp.x, y: y(coopRow), w: ORI_W, label: fit(`${cp.c.rejected ? '⚠ ' : ''}${label}`, ORI_W - 14),
          full: `${cp.c.name}: ${cp.c.plots} plots${tonnes ? `, producing${tonnes.replace(' ·', '')} a year (declared)` : ''}${cp.c.rejected ? `, ${cp.c.rejected} rejected by the EUDR checks` : ''}`,
          fill: tokens.color.brandGreen100, text: '#1f4741', stroke: cp.c.rejected ? tokens.color.danger : undefined,
        }
        boxes.push(cb)
        for (const o of cp.c.under) lines.push({ a: o, b: cb })
      }
      const width2 = Math.max(total, nextCoop - GAP_X + PAD)
      return {
        boxes, lines, width: width2, height: (coops.size ? y(coopRow) : y(3 + tiers)) + BOX_H + PAD,
        sharedCount: placed.filter((p) => p.ms.length > 1).length, hasFarms: coops.size > 0,
      }
    }, [structure, countryName])

    const path = (a: Box_, b: Box_) => {
      const x1 = a.x + a.w / 2
      const y1 = a.y + BOX_H
      const x2 = b.x + b.w / 2
      const y2 = b.y
      const mid = (y1 + y2) / 2
      return `M${x1},${y1} C${x1},${mid} ${x2},${mid} ${x2},${y2}`
    }

    return (
      <Box>
        <svg
          ref={ref}
          xmlns="http://www.w3.org/2000/svg"
          width={layout.width}
          height={layout.height}
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          role="img"
          aria-label={`${structure.company.name}: legal entities, their materials and origins. ${layout.sharedCount} origins are shared between materials.`}
          style={{ display: 'block', fontFamily: tokens.font.sans }}
        >
          <rect width={layout.width} height={layout.height} fill={tokens.color.ink0} />
          {layout.lines.map((l, i) => (
            <path key={i} d={path(l.a, l.b)} fill="none" stroke={l.strong ? tokens.color.warning : tokens.color.ink300} strokeWidth={l.strong ? 1.6 : 1.1} />
          ))}
          {layout.boxes.map((b, i) => (
            <g key={i}>
              <title>{b.full}</title>
              <rect x={b.x} y={b.y} width={b.w} height={BOX_H} rx={4} fill={b.fill} stroke={b.stroke ?? 'none'} strokeWidth={b.stroke ? 1.5 : 0} />
              <text x={b.x + 7} y={b.y + 16} fontSize={12} fontWeight={b.bold ? 600 : 500} fill={b.text}>{b.label}</text>
            </g>
          ))}
        </svg>
        {structure.totals && (
          <Box sx={{ px: 4, pt: 2, font: `500 13px ${tokens.font.sans}` }}>
            {structure.company.name}: {structure.totals.plots.toLocaleString('en-GB')} plots behind its origins
            {structure.totals.rejected ? `, ${structure.totals.rejected} rejected` : ''}
            {structure.totals.productionT ? `, producing ${Math.round(structure.totals.productionT).toLocaleString('en-GB')} t a year (declared` : ''}
            {structure.totals.productionT && structure.totals.productionByCrop
              ? `: ${Object.entries(structure.totals.productionByCrop).map(([crop, t]) => `${crop} ${Math.round(t).toLocaleString('en-GB')} t`).join(', ')})` : structure.totals.productionT ? ')' : ''}
            . Each plot counted once.
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 4, px: 4, py: 2, font: `400 12px ${tokens.font.sans}`, color: 'text.secondary', flexWrap: 'wrap' }}>
          <span>Shared origins have an amber outline and start with “×n”: one origin, used by n materials across the group.</span>
          <span>⚠ marks an origin whose check failed.</span>
          {layout.hasFarms && <span>Green: the farmer cooperatives behind an origin, with their plots; a red outline has plots rejected by the EUDR checks.</span>}
        </Box>
      </Box>
    )
  },
)
